import { useEffect, useState } from "react";
import { opApi, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Check, Lock } from "lucide-react";

// The "wall": visual grid of industry cart types. Live ones are selectable; the rest are coming-soon.
// Switching is non-destructive (backend only flips a field), so a business can move between types freely.
export const CartTypeChooser = ({ onChanged }) => {
  const [types, setTypes] = useState([]);
  const [current, setCurrent] = useState(null);
  const [pending, setPending] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    opApi.get("/tenant/cart-type").then(({ data }) => { setCurrent(data.cart_type); setTypes(data.cart_types || []); }).catch(() => {});
  }, []);

  const confirmSwitch = () => {
    setSaving(true);
    opApi.patch("/tenant/cart-type", { cart_type: pending.key })
      .then(() => { toast.success(`Switched to ${pending.label}`); setCurrent(pending.key); const k = pending.key; setPending(null); onChanged && onChanged(k); })
      .catch((e) => toast.error(apiError(e.response?.data?.detail)))
      .finally(() => setSaving(false));
  };

  return (
    <div data-testid="cart-type-chooser">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {types.map((c) => {
          const active = c.key === current;
          return (
            <button key={c.key} type="button" disabled={!c.live || active}
              onClick={() => { if (c.live && !active) setPending(c); }}
              className={`text-left border p-4 rounded-none transition-colors relative ${active ? "border-primary bg-primary/5" : c.live ? "border-border hover:border-primary" : "border-dashed border-border opacity-60 cursor-not-allowed"}`}
              data-testid={`cart-type-${c.key}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-sm">{c.label}</span>
                {active && <Check className="h-4 w-4 text-primary" />}
                {!c.live && <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
              </div>
              <p className="text-xs text-muted-foreground mt-1 mb-2">{c.blurb}</p>
              {active && <span className="text-[10px] uppercase tracking-wide text-primary font-semibold">Current</span>}
              {!c.live && <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Coming soon</span>}
            </button>
          );
        })}
      </div>
      <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <DialogContent className="rounded-none" data-testid="cart-switch-dialog">
          <DialogHeader>
            <DialogTitle>Switch to {pending?.label}?</DialogTitle>
            <DialogDescription>Your storefront and dashboard adapt to this type. Nothing is deleted — you can switch back anytime.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setPending(null)}>Cancel</Button>
            <Button className="rounded-none" onClick={confirmSwitch} disabled={saving} data-testid="confirm-cart-switch">{saving ? "Switching…" : "Switch"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
